import React, { useMemo, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { FormGroup, Input } from 'src/components/ui';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { useLingui } from '@lingui/react';
import { useFormatter } from '../../util/applyFormatting';
import { useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import useWrapInput from './utils/useWrapInput';
import classnames from '../../util/classnames';
import useTestId from '../../hooks/useTestId';
import { useAutofillSafeName } from '../../hooks/useAutofillSafeName';

export default function TextInput(props) {
  const {
    id,
    required,
    disabled,
    readonly,
    autofocus,
    registry,
    formData,
    fieldPathId,
    uiSchema: {
      'ui:requiredIf': uiRequiredIf,
      'ui:staticText': staticText,
      'ui:staticTransform': staticTransform = null,
      'ui:classNames': classNames,
      'ui:cellClassName': cellClassName,
      'ui:prefix': prefix,
      'ui:sufix': sufix,
      'ui:placeHolder': placeholder,
      'ui:disableIf': uiDisableIf,
      'ui:tooltipText': tooltipText = '',
      'ui:onChange': onChangeExpr,
    },
    schema,
  } = props;
  const formContext = registry?.formContext;

  const { minLength, maxLength, 'map:field': mapField, _key: key } = schema;

  const rootFormData = useSideChannelSubscription(formContext.sideChannel, 0);
  const { text, onChange, formatter } = useTextInputHooks(props, rootFormData);
  const testIdProps = useTestId(id);

  const dataPath = useMemo(() => getPathFromId(id), [id]);
  const defaultValue = '';
  const dataValue = props.value || defaultValue;

  const requiredIfJnx = useJnx(uiRequiredIf);
  const requiredIf = useMemo(
    () =>
      requiredIfJnx &&
      requiredIfJnx.eval(rootFormData || {}, dataPath, {
        root: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );

  const disableIfJnx = useJnx(uiDisableIf);
  const disableIf = useMemo(
    () =>
      disableIfJnx &&
      disableIfJnx.eval(rootFormData || {}, dataPath, {
        rootData: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );

  const staticTransformJnx = useJnx(staticTransform);

  const inputType = useMemo(() => {
    switch (schema.type) {
      // TODO handle more types here
      default:
        return 'text';
    }
  }, [schema.type]);

  const isNumeric = schema.type === 'number' || schema.type === 'integer';

  const _staticText = useMemo(() => {
    if (staticText) {
      if (staticTransform) {
        return staticTransformJnx.eval(text, '', { value: text });
      }
      return text;
    }
    return null;
  }, [text, staticText, staticTransform, staticTransformJnx]);

  const autofillSafeName = useAutofillSafeName();

  return (
    <FormGroup className={classnames({ 'input-wrapper': !staticText })}>
      {staticText ? (
        <div className={classnames(classNames, cellClassName)}>{_staticText}</div>
      ) : (
        useWrapInput({
          input: (
            <Input
              disabled={disabled || disableIf}
              type={inputType}
              name={autofillSafeName}
              autoComplete="off"
              placeholder={`${placeholder || ''}`}
              className={classnames('custom', { 'text-right': isNumeric }, classNames, cellClassName)}
              value={text}
              minLength={minLength || ''}
              maxLength={maxLength || ''}
              required={required || requiredIf}
              onChange={onChange}
              readOnly={readonly}
              autoFocus={autofocus}
              id={id}
              {...testIdProps}
            />
          ),
          prefix,
          sufix,
          tooltipText,
          rootFormData,
          dataPath,
          fieldId: id,
          formContext,
          formData: text,
          fieldSchema: schema,
        })
      )}
    </FormGroup>
  );
}

function useTextInputHooks({ id, schema: { format, title }, value, onChange: propsOnChange, uiSchema, registry }, rootFormData) {
  const { setFormDataValues, bindings } = registry?.formContext ?? {};
  const { i18n } = useLingui();
  const [current, setCurrent] = useState(value);
  const formatter = useFormatter(format);
  const functionBinds = { set: setFormDataValues };
  const onChangeExpr = uiSchema['ui:onChange'];
  const onChangeJnx = useJnx(onChangeExpr, { functionBinds });

  useDebouncedEffect(
    () => {
      if (!current) return;

      if (current.value !== value && !(isBlank(current.value) && isBlank(value))) {
        propsOnChange(current.value);

        if (onChangeExpr) {
          setTimeout(() => {
            onChangeJnx.evalAsync(onChangeExpr, '', { ...bindings, newValue: current.value, rootFormData });
          }, 100);
        }
      }

      // Text the formatter rejects parses to nothing. When the field was already empty no value change
      // follows to re-render the text, so clear it here or the input keeps showing the rejected text.
      if (isBlank(current.value) && current.text) {
        setCurrent({ value: current.value, text: '' });
      }
    },
    [current],
    450
  );

  const onChange = ({ target: { value: text } }) => {
    setCurrent({
      value: formatter.parse(text),
      text: formatter.preFormat ? formatter.apply(text, i18n.locale) : text,
    });
  };

  useEffect(() => {
    setCurrent({
      value,
      text: formatter.apply(value != null ? value : '', i18n.locale),
    });
  }, [value, formatter, i18n.locale]);

  return { text: current?.text != null ? current.text : '', onChange, formatter };
}

function isBlank(value) {
  return value === undefined || value === null || value === '';
}
